import { useState, useEffect, useCallback } from 'react';
import adminApiService from '../../services/adminApiService';
import ActivityMetadataViewer from '../common/ActivityMetadataViewer';

const AdminActivityLogs = () => {
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [logs, setLogs] = useState([]);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState(null);
  const [moduleOptions, setModuleOptions] = useState({ modules: [], typesByModule: {}, statuses: [] });

  // Local input state for smooth typing & debouncing
  const [searchInput, setSearchInput] = useState('');
  const [usernameInput, setUsernameInput] = useState('');

  // Filter state
  const [filters, setFilters] = useState({
    searchTerm: '',
    userId: '',
    username: '',
    userRole: '',
    module: '',
    activityType: '',
    status: '',
    datePreset: 'last7days',
    startDate: '',
    endDate: '',
    page: 0,
    size: 20,
    sortBy: 'createdAt',
    sortDir: 'DESC'
  });

  // Pagination meta
  const [pagination, setPagination] = useState({
    currentPage: 0,
    totalPages: 0,
    totalElements: 0,
    pageSize: 20,
    hasNext: false,
    hasPrevious: false
  });

  // Modal detail state
  const [selectedLog, setSelectedLog] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  // Helper to format a local Date object into 'YYYY-MM-DDTHH:mm:ss'
  const toLocalISOString = (date) => {
    const pad = (n) => String(n).padStart(2, '0');
    const year = date.getFullYear();
    const month = pad(date.getMonth() + 1);
    const day = pad(date.getDate());
    const hours = pad(date.getHours());
    const minutes = pad(date.getMinutes());
    const seconds = pad(date.getSeconds());
    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
  };

  // Initialize date ranges based on preset using local time boundaries
  const applyDatePreset = (preset) => {
    const now = new Date();
    let start = '';
    let end = '';

    if (preset === 'today') {
      const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0);
      const todayEnd = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);
      start = toLocalISOString(todayStart);
      end = toLocalISOString(todayEnd);
    } else if (preset === 'yesterday') {
      const yesterdayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1, 0, 0, 0);
      const yesterdayEnd = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1, 23, 59, 59);
      start = toLocalISOString(yesterdayStart);
      end = toLocalISOString(yesterdayEnd);
    } else if (preset === 'last7days') {
      const sevenDaysAgo = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 6, 0, 0, 0);
      const todayEnd = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);
      start = toLocalISOString(sevenDaysAgo);
      end = toLocalISOString(todayEnd);
    } else if (preset === 'last30days') {
      const thirtyDaysAgo = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 29, 0, 0, 0);
      const todayEnd = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);
      start = toLocalISOString(thirtyDaysAgo);
      end = toLocalISOString(todayEnd);
    } else if (preset === 'all') {
      start = '';
      end = '';
    }

    return { start, end };
  };

  // Debounce search and username typing (350ms)
  useEffect(() => {
    const timer = setTimeout(() => {
      setFilters((prev) => {
        if (prev.searchTerm === searchInput && prev.username === usernameInput) {
          return prev;
        }
        return {
          ...prev,
          searchTerm: searchInput,
          username: usernameInput,
          page: 0
        };
      });
    }, 350);

    return () => clearTimeout(timer);
  }, [searchInput, usernameInput]);

  // Load Module & Type options
  useEffect(() => {
    const loadOptionsAndStats = async () => {
      try {
        const [optionsRes, statsRes] = await Promise.all([
          adminApiService.getActivityModuleOptions(),
          adminApiService.getActivityStats()
        ]);
        if (optionsRes.success && optionsRes.data) {
          setModuleOptions(optionsRes.data);
        }
        if (statsRes.success && statsRes.data) {
          setStats(statsRes.data);
        }
      } catch (err) {
        console.error('Failed to load initial options/stats:', err);
      }
    };
    loadOptionsAndStats();
  }, []);

  // Fetch Logs with active filters
  const fetchLogs = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params = {
        page: filters.page,
        size: filters.size,
        sortBy: filters.sortBy,
        sortDir: filters.sortDir
      };

      if (filters.searchTerm) params.searchTerm = filters.searchTerm;
      if (filters.userId) params.userId = filters.userId;
      if (filters.username) params.username = filters.username;
      if (filters.userRole) params.userRole = filters.userRole;
      if (filters.module) params.module = filters.module;
      if (filters.activityType) params.activityType = filters.activityType;
      if (filters.status) params.status = filters.status;

      // Handle dates
      if (filters.datePreset !== 'custom') {
        const { start, end } = applyDatePreset(filters.datePreset);
        if (start) params.startDate = start;
        if (end) params.endDate = end;
      } else {
        if (filters.startDate) params.startDate = filters.startDate;
        if (filters.endDate) params.endDate = filters.endDate;
      }

      const response = await adminApiService.getActivityLogs(params);

      if (response.success) {
        setLogs(response.data || []);
        setPagination({
          currentPage: response.currentPage || 0,
          totalPages: response.totalPages || 0,
          totalElements: response.totalElements || 0,
          pageSize: response.pageSize || filters.size,
          hasNext: response.hasNext || false,
          hasPrevious: response.hasPrevious || false
        });
      } else {
        setError(response.message || 'Failed to fetch activity logs');
      }
    } catch (err) {
      console.error('Error fetching activity logs:', err);
      setError('An error occurred while loading activity logs. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  // Handle Log Detail View
  const handleViewDetail = async (id) => {
    try {
      setLoadingDetail(true);
      setModalOpen(true);
      const res = await adminApiService.getActivityLogById(id);
      if (res.success && res.data) {
        setSelectedLog(res.data);
      }
    } catch (err) {
      console.error('Failed to load log detail:', err);
    } finally {
      setLoadingDetail(false);
    }
  };

  // Handle CSV Export
  const handleExportCsv = async () => {
    try {
      setExporting(true);
      const params = {};
      if (filters.searchTerm) params.searchTerm = filters.searchTerm;
      if (filters.userId) params.userId = filters.userId;
      if (filters.username) params.username = filters.username;
      if (filters.userRole) params.userRole = filters.userRole;
      if (filters.module) params.module = filters.module;
      if (filters.activityType) params.activityType = filters.activityType;
      if (filters.status) params.status = filters.status;

      if (filters.datePreset !== 'custom') {
        const { start, end } = applyDatePreset(filters.datePreset);
        if (start) params.startDate = start;
        if (end) params.endDate = end;
      } else {
        if (filters.startDate) params.startDate = filters.startDate;
        if (filters.endDate) params.endDate = filters.endDate;
      }

      const blob = await adminApiService.exportActivityLogs(params);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `user_activity_logs_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      setShowExportModal(false);
    } catch (err) {
      console.error('Error exporting logs:', err);
      alert('Failed to export activity logs.');
    } finally {
      setExporting(false);
    }
  };

  // Reset Filters
  const handleResetFilters = () => {
    setSearchInput('');
    setUsernameInput('');
    setFilters({
      searchTerm: '',
      userId: '',
      username: '',
      userRole: '',
      module: '',
      activityType: '',
      status: '',
      datePreset: 'last7days',
      startDate: '',
      endDate: '',
      page: 0,
      size: 20,
      sortBy: 'createdAt',
      sortDir: 'DESC'
    });
  };

  const formatDate = (isoString) => {
    if (!isoString) return '-';
    try {
      return new Date(isoString).toLocaleString('en-IN', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });
    } catch {
      return isoString;
    }
  };

  const getModuleBadge = (module) => {
    const colors = {
      AUTH: 'bg-indigo-100 text-indigo-800 border-indigo-200',
      HOTEL_SEARCH: 'bg-blue-100 text-blue-800 border-blue-200',
      PRE_BOOK: 'bg-cyan-100 text-cyan-800 border-cyan-200',
      BOOKING: 'bg-emerald-100 text-emerald-800 border-emerald-200',
      CANCELLATION: 'bg-rose-100 text-rose-800 border-rose-200',
      WALLET_PAYMENT: 'bg-amber-100 text-amber-800 border-amber-200',
      SUPPORT_TICKET: 'bg-purple-100 text-purple-800 border-purple-200',
      ADMIN_SETTINGS: 'bg-slate-100 text-slate-800 border-slate-200',
      AGENT_MANAGEMENT: 'bg-teal-100 text-teal-800 border-teal-200'
    };
    return colors[module] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6">
        
        {/* Page Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6 pb-4 border-b border-gray-200">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <i className="fas fa-history text-purple-600"></i> User Activity & Audit Logs
            </h1>
            <p className="mt-1 text-sm text-gray-500">
              Track and audit all administrative, agent, and security activities (Retained for 90 Days)
            </p>
          </div>
          <div className="mt-4 md:mt-0 flex gap-3">
            <button
              onClick={fetchLogs}
              disabled={loading}
              className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
            >
              <i className={`fas fa-sync-alt mr-2 ${loading ? 'animate-spin' : ''}`}></i> Refresh
            </button>
            <button
              onClick={() => setShowExportModal(true)}
              disabled={exporting || loading}
              className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-lg text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
            >
              <i className={`fas ${exporting ? 'fa-spinner fa-spin' : 'fa-file-csv'} mr-2`}></i>
              {exporting ? 'Exporting...' : 'Export to CSV'}
            </button>
          </div>
        </div>

        {/* Stats Overview */}
        {stats && (
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-3 mb-6">
            <div className="bg-white overflow-hidden shadow rounded-xl p-5 border border-gray-100 flex items-center">
              <div className="p-3 rounded-lg bg-purple-100 text-purple-600 mr-4">
                <i className="fas fa-layer-group text-2xl"></i>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500 truncate">Total Active Logs</dt>
                <dd className="text-2xl font-semibold text-gray-900">{stats.totalActivities?.toLocaleString() || 0}</dd>
              </div>
            </div>

            <div className="bg-white overflow-hidden shadow rounded-xl p-5 border border-gray-100 flex items-center">
              <div className="p-3 rounded-lg bg-rose-100 text-rose-600 mr-4">
                <i className="fas fa-exclamation-triangle text-2xl"></i>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500 truncate">Failures (Last 24h)</dt>
                <dd className="text-2xl font-semibold text-rose-600">{stats.failedLast24Hours || 0}</dd>
              </div>
            </div>

            <div className="bg-white overflow-hidden shadow rounded-xl p-5 border border-gray-100 flex items-center">
              <div className="p-3 rounded-lg bg-emerald-100 text-emerald-600 mr-4">
                <i className="fas fa-filter text-2xl"></i>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500 truncate">Filtered Matching Records</dt>
                <dd className="text-2xl font-semibold text-emerald-600">{pagination.totalElements.toLocaleString()}</dd>
              </div>
            </div>
          </div>
        )}

        {/* Filter Controls Card */}
        <div className="bg-white shadow rounded-xl p-5 mb-6 border border-gray-200">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            
            {/* Username / Email Filter */}
            <div>
              <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1">Username / Email</label>
              <div className="relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <i className="fas fa-user text-gray-400 text-sm"></i>
                </div>
                <input
                  type="text"
                  placeholder="Filter by username or email..."
                  value={usernameInput}
                  onChange={(e) => setUsernameInput(e.target.value)}
                  className="focus:ring-purple-500 focus:border-purple-500 block w-full pl-9 sm:text-sm border-gray-300 rounded-lg p-2 border"
                />
              </div>
            </div>

            {/* General Search Input */}
            <div>
              <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1">Keyword / IP / Trace</label>
              <div className="relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <i className="fas fa-search text-gray-400 text-sm"></i>
                </div>
                <input
                  type="text"
                  placeholder="IP, trace ID, description..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  className="focus:ring-purple-500 focus:border-purple-500 block w-full pl-9 sm:text-sm border-gray-300 rounded-lg p-2 border"
                />
              </div>
            </div>

            {/* Module Filter */}
            <div>
              <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1">Module</label>
              <select
                value={filters.module}
                onChange={(e) => setFilters({ ...filters, module: e.target.value, activityType: '', page: 0 })}
                className="focus:ring-purple-500 focus:border-purple-500 block w-full sm:text-sm border-gray-300 rounded-lg p-2 border bg-white"
              >
                <option value="">All Modules</option>
                {moduleOptions.modules?.map((m) => (
                  <option key={m.name} value={m.name}>{m.displayName}</option>
                ))}
              </select>
            </div>

            {/* Action Type Filter */}
            <div>
              <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1">Activity Action</label>
              <select
                value={filters.activityType}
                onChange={(e) => setFilters({ ...filters, activityType: e.target.value, page: 0 })}
                className="focus:ring-purple-500 focus:border-purple-500 block w-full sm:text-sm border-gray-300 rounded-lg p-2 border bg-white"
              >
                <option value="">All Activity Types</option>
                {filters.module && moduleOptions.typesByModule[filters.module]
                  ? moduleOptions.typesByModule[filters.module].map((t) => (
                      <option key={t.name} value={t.name}>{t.description}</option>
                    ))
                  : Object.values(moduleOptions.typesByModule).flat().map((t) => (
                      <option key={t.name} value={t.name}>{t.description}</option>
                    ))}
              </select>
            </div>

            {/* Time Range Preset */}
            <div>
              <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1">Time Range</label>
              <select
                value={filters.datePreset}
                onChange={(e) => setFilters({ ...filters, datePreset: e.target.value, page: 0 })}
                className="focus:ring-purple-500 focus:border-purple-500 block w-full sm:text-sm border-gray-300 rounded-lg p-2 border bg-white"
              >
                <option value="today">Today</option>
                <option value="yesterday">Yesterday</option>
                <option value="last7days">Last 7 Days</option>
                <option value="last30days">Last 30 Days</option>
                <option value="all">All Available (90 Days)</option>
                <option value="custom">Custom Date Range</option>
              </select>
            </div>

            {/* Status Filter */}
            <div>
              <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1">Status</label>
              <select
                value={filters.status}
                onChange={(e) => setFilters({ ...filters, status: e.target.value, page: 0 })}
                className="focus:ring-purple-500 focus:border-purple-500 block w-full sm:text-sm border-gray-300 rounded-lg p-2 border bg-white"
              >
                <option value="">All Statuses</option>
                <option value="SUCCESS">Success Only</option>
                <option value="FAILED">Failed Only</option>
              </select>
            </div>

            {/* Role Filter */}
            <div>
              <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1">User Role</label>
              <select
                value={filters.userRole}
                onChange={(e) => setFilters({ ...filters, userRole: e.target.value, page: 0 })}
                className="focus:ring-purple-500 focus:border-purple-500 block w-full sm:text-sm border-gray-300 rounded-lg p-2 border bg-white"
              >
                <option value="">All Roles</option>
                <option value="ROLE_ADMIN">Admin</option>
                <option value="ROLE_AGENT">Agent</option>
                <option value="ROLE_SUPPORT">Support Executive</option>
              </select>
            </div>

            {/* Filter Actions */}
            <div className="flex items-end gap-2">
              <button
                type="button"
                onClick={handleResetFilters}
                className="w-full inline-flex justify-center items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-lg text-gray-700 bg-gray-100 hover:bg-gray-200"
              >
                <i className="fas fa-undo mr-2"></i> Reset Filters
              </button>
            </div>

            {/* Custom Date Pickers (if custom selected) */}
            {filters.datePreset === 'custom' && (
              <>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1">Start Date</label>
                  <input
                    type="datetime-local"
                    value={filters.startDate}
                    onChange={(e) => setFilters({ ...filters, startDate: e.target.value, page: 0 })}
                    className="focus:ring-purple-500 focus:border-purple-500 block w-full sm:text-sm border-gray-300 rounded-lg p-2 border"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1">End Date</label>
                  <input
                    type="datetime-local"
                    value={filters.endDate}
                    onChange={(e) => setFilters({ ...filters, endDate: e.target.value, page: 0 })}
                    className="focus:ring-purple-500 focus:border-purple-500 block w-full sm:text-sm border-gray-300 rounded-lg p-2 border"
                  />
                </div>
              </>
            )}

          </div>
        </div>

        {/* Error Alert */}
        {error && (
          <div className="rounded-lg bg-red-50 p-4 mb-6 border border-red-200">
            <div className="flex">
              <div className="flex-shrink-0">
                <i className="fas fa-times-circle text-red-500"></i>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">{error}</h3>
              </div>
            </div>
          </div>
        )}

        {/* Logs Data Table */}
        <div className="bg-white shadow overflow-hidden rounded-xl border border-gray-200">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Timestamp / Date</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User / Role</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Module</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Activity Action</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Client IP</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Duration</th>
                  <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan="8" className="px-6 py-12 text-center text-gray-500">
                      <div className="flex justify-center items-center gap-3">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-600"></div>
                        <span>Loading activity logs...</span>
                      </div>
                    </td>
                  </tr>
                ) : logs.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="px-6 py-12 text-center text-gray-500">
                      <i className="fas fa-folder-open text-3xl mb-3 text-gray-300 block"></i>
                      No activity logs found matching your criteria.
                    </td>
                  </tr>
                ) : (
                  logs.map((logItem) => (
                    <tr key={logItem.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap text-xs text-gray-600 font-mono">
                        {formatDate(logItem.createdAt)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-semibold text-gray-900">{logItem.username || 'Anonymous'}</div>
                        <div className="text-xs text-gray-500 flex items-center gap-1 mt-0.5">
                          {logItem.userId && <span className="bg-gray-100 px-1.5 py-0.5 rounded text-gray-600">{logItem.userId}</span>}
                          <span className="text-purple-600 font-medium">{logItem.userRole?.replace('ROLE_', '')}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getModuleBadge(logItem.module)}`}>
                          {logItem.moduleDisplayName || logItem.module}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-700">
                        <div className="font-medium text-gray-900">{logItem.activityDescription || logItem.activityType}</div>
                        <div className="text-xs text-gray-500 truncate max-w-xs">{logItem.description}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-xs text-gray-500 font-mono">
                        {logItem.ipAddress || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {logItem.status === 'SUCCESS' ? (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            <i className="fas fa-check-circle mr-1 text-green-600"></i> Success
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800" title={logItem.errorMessage}>
                            <i className="fas fa-exclamation-circle mr-1 text-red-600"></i> Failed
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-xs text-gray-500">
                        {logItem.durationMs ? `${logItem.durationMs}ms` : '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <button
                          onClick={() => handleViewDetail(logItem.id)}
                          className="text-purple-600 hover:text-purple-900 bg-purple-50 hover:bg-purple-100 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors"
                        >
                          Details <i className="fas fa-chevron-right ml-1"></i>
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination Footer */}
          <div className="bg-white px-4 py-3 border-t border-gray-200 sm:px-6 flex items-center justify-between">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => setFilters({ ...filters, page: filters.page - 1 })}
                disabled={!pagination.hasPrevious}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                Previous
              </button>
              <button
                onClick={() => setFilters({ ...filters, page: filters.page + 1 })}
                disabled={!pagination.hasNext}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                Next
              </button>
            </div>

            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Showing page <span className="font-semibold">{pagination.currentPage + 1}</span> of{' '}
                  <span className="font-semibold">{pagination.totalPages || 1}</span> ({pagination.totalElements.toLocaleString()} total entries)
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setFilters({ ...filters, page: filters.page - 1 })}
                  disabled={!pagination.hasPrevious}
                  className="relative inline-flex items-center px-3 py-1.5 border border-gray-300 text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-40"
                >
                  <i className="fas fa-chevron-left mr-1"></i> Previous
                </button>
                <button
                  onClick={() => setFilters({ ...filters, page: filters.page + 1 })}
                  disabled={!pagination.hasNext}
                  className="relative inline-flex items-center px-3 py-1.5 border border-gray-300 text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-40"
                >
                  Next <i className="fas fa-chevron-right ml-1"></i>
                </button>
              </div>
            </div>
          </div>
        </div>

      </div>

      {/* Log Detail Modal */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-gray-900 bg-opacity-60 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            
            {/* Modal Header */}
            <div className="px-6 py-4 bg-gradient-to-r from-purple-600 to-indigo-700 text-white flex justify-between items-center">
              <div>
                <h3 className="text-lg font-bold flex items-center gap-2">
                  <i className="fas fa-info-circle"></i> User Activity Log Details
                </h3>
                <p className="text-xs text-purple-200 mt-0.5">Audit ID: {selectedLog?.id}</p>
              </div>
              <button
                onClick={() => { setModalOpen(false); setSelectedLog(null); }}
                className="text-purple-200 hover:text-white text-xl p-1 rounded-lg"
              >
                <i className="fas fa-times"></i>
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 overflow-y-auto flex-1 space-y-6">
              {loadingDetail ? (
                <div className="text-center py-12">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-purple-600 mx-auto"></div>
                  <p className="text-sm text-gray-500 mt-3">Loading log details...</p>
                </div>
              ) : selectedLog ? (
                <>
                  {/* Key Properties Grid */}
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4 bg-gray-50 p-4 rounded-xl border border-gray-200 text-sm">
                    <div>
                      <span className="text-xs text-gray-500 block uppercase">Username</span>
                      <span className="font-semibold text-gray-900">{selectedLog.username || 'Anonymous'}</span>
                    </div>
                    <div>
                      <span className="text-xs text-gray-500 block uppercase">User Role</span>
                      <span className="font-semibold text-purple-600">{selectedLog.userRole || '-'}</span>
                    </div>
                    <div>
                      <span className="text-xs text-gray-500 block uppercase">Module</span>
                      <span className="font-semibold text-gray-900">{selectedLog.moduleDisplayName || selectedLog.module}</span>
                    </div>
                    <div>
                      <span className="text-xs text-gray-500 block uppercase">Action Type</span>
                      <span className="font-semibold text-gray-900">{selectedLog.activityTypeDescription || selectedLog.activityType}</span>
                    </div>
                    <div>
                      <span className="text-xs text-gray-500 block uppercase">Status</span>
                      <span className={`font-semibold ${selectedLog.status === 'SUCCESS' ? 'text-green-600' : 'text-red-600'}`}>
                        {selectedLog.status}
                      </span>
                    </div>
                    <div>
                      <span className="text-xs text-gray-500 block uppercase">Client IP</span>
                      <span className="font-mono text-gray-900">{selectedLog.ipAddress || '-'}</span>
                    </div>
                    <div>
                      <span className="text-xs text-gray-500 block uppercase">HTTP Method & URI</span>
                      <span className="font-mono text-xs text-gray-700">{selectedLog.httpMethod} {selectedLog.requestUri}</span>
                    </div>
                    <div>
                      <span className="text-xs text-gray-500 block uppercase">Execution Duration</span>
                      <span className="text-gray-900 font-medium">{selectedLog.durationMs ? `${selectedLog.durationMs} ms` : '-'}</span>
                    </div>
                    <div>
                      <span className="text-xs text-gray-500 block uppercase">Trace ID</span>
                      <span className="font-mono text-xs text-gray-600 truncate block">{selectedLog.traceId || '-'}</span>
                    </div>
                  </div>

                  {/* Description */}
                  <div>
                    <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1">Description</h4>
                    <p className="text-sm text-gray-800 bg-white p-3 rounded-lg border border-gray-200">
                      {selectedLog.description}
                    </p>
                  </div>

                  {/* Error Message if Failed */}
                  {selectedLog.errorMessage && (
                    <div>
                      <h4 className="text-xs font-semibold text-red-700 uppercase tracking-wider mb-1">Error Information</h4>
                      <div className="text-sm text-red-800 bg-red-50 p-3 rounded-lg border border-red-200 font-mono">
                        {selectedLog.errorMessage}
                      </div>
                    </div>
                  )}

                  {/* Sanitized Metadata Payload */}
                  <ActivityMetadataViewer
                    metadata={selectedLog.metadata}
                    theme="purple"
                    title="Event Metadata (Sanitized)"
                  />

                  {/* Audit Trail Context */}
                  <div className="text-xs text-gray-500 pt-2 border-t border-gray-200 grid grid-cols-2 gap-2">
                    <div><strong>Created At:</strong> {formatDate(selectedLog.createdAt)} by {selectedLog.createdBy || 'System'}</div>
                    <div><strong>Updated At:</strong> {formatDate(selectedLog.updatedAt)} by {selectedLog.updatedBy || 'System'}</div>
                  </div>
                </>
              ) : null}
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-3 bg-gray-50 border-t border-gray-200 flex justify-end">
              <button
                type="button"
                onClick={() => { setModalOpen(false); setSelectedLog(null); }}
                className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 text-sm font-medium rounded-lg"
              >
                Close
              </button>
            </div>

          </div>
        </div>
      )}

      {/* Export Confirmation Modal */}
      {showExportModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-gray-900 bg-opacity-60 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="px-6 py-4 bg-gradient-to-r from-purple-600 to-indigo-700 text-white flex justify-between items-center">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-lg bg-white bg-opacity-20 flex items-center justify-center">
                  <i className="fas fa-file-csv text-white text-base"></i>
                </div>
                <h3 className="text-lg font-bold">Export Activity Logs</h3>
              </div>
              <button
                onClick={() => setShowExportModal(false)}
                disabled={exporting}
                className="text-purple-200 hover:text-white text-xl p-1 rounded-lg"
              >
                <i className="fas fa-times"></i>
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 space-y-4">
              <p className="text-sm text-gray-600">
                Are you sure you want to export and download the activity logs as a <strong>CSV</strong> file?
              </p>

              <div className="bg-purple-50 border border-purple-100 rounded-xl p-4 space-y-2 text-xs text-gray-700">
                <div className="flex justify-between">
                  <span className="text-gray-500 font-medium">Estimated Records:</span>
                  <span className="font-bold text-purple-700">{pagination.totalElements.toLocaleString()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500 font-medium">Time Range:</span>
                  <span className="font-semibold text-gray-800 capitalize">
                    {filters.datePreset === 'all'
                      ? 'All Available (90 Days)'
                      : filters.datePreset === 'custom'
                      ? 'Custom Range'
                      : filters.datePreset}
                  </span>
                </div>
                {filters.module && (
                  <div className="flex justify-between">
                    <span className="text-gray-500 font-medium">Module:</span>
                    <span className="font-semibold text-gray-800">{filters.module}</span>
                  </div>
                )}
                {filters.status && (
                  <div className="flex justify-between">
                    <span className="text-gray-500 font-medium">Status:</span>
                    <span className="font-semibold text-gray-800">{filters.status}</span>
                  </div>
                )}
                {filters.username && (
                  <div className="flex justify-between">
                    <span className="text-gray-500 font-medium">User:</span>
                    <span className="font-semibold text-gray-800">{filters.username}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setShowExportModal(false)}
                disabled={exporting}
                className="px-4 py-2 border border-gray-300 text-gray-700 hover:bg-gray-100 text-sm font-medium rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleExportCsv}
                disabled={exporting}
                className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white text-sm font-medium rounded-lg shadow-sm transition-colors disabled:opacity-50"
              >
                <i className={`fas ${exporting ? 'fa-spinner fa-spin' : 'fa-download'}`}></i>
                {exporting ? 'Downloading...' : 'Yes, Download CSV'}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default AdminActivityLogs;
